import api from "@/lib/axiosConfig";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

const baseRoute = "back/api/multiplesample-mouvements";

const _multiplesampleMouvementSchema = z.object({
  id: z.string(),
  multiplesampleId: z.string(),
  instanceId: z.string(),
  description: z.string(),
  lieu: z.string(),
  dateDepart: z.string(),
  dateRetour: z.string(),
  constatEtatDepart: z.boolean(),
  constatEtatRetour: z.boolean(),
  notes: z.string(),
  sequence: z.number(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

export type MultiplesampleMouvement = z.infer<
  typeof _multiplesampleMouvementSchema
>;

const _createMultiplesampleMouvementSchema =
  _multiplesampleMouvementSchema.omit({
    id: true,
    instanceId: true,
    sequence: true,
    createdAt: true,
    updatedAt: true,
  });

export type CreateMultiplesampleMouvement = z.infer<
  typeof _createMultiplesampleMouvementSchema
>;

export type EditMultiplesampleMouvement = Omit<
  CreateMultiplesampleMouvement,
  "multiplesampleId"
>;

const mouvementsQueryKey = (multiplesampleId: string | undefined) => [
  "multiplesample-mouvements",
  multiplesampleId,
];

async function getMultiplesampleMouvements(multiplesampleId: string) {
  const { data } = await api.get<MultiplesampleMouvement[]>(baseRoute, {
    params: { multiplesampleId },
  });
  return data;
}

export function useGetMultiplesampleMouvements(
  multiplesampleId: string | undefined,
) {
  return useQuery({
    queryKey: mouvementsQueryKey(multiplesampleId),
    queryFn: () =>
      multiplesampleId
        ? getMultiplesampleMouvements(multiplesampleId)
        : Promise.resolve([]),
    enabled: !!multiplesampleId,
  });
}

async function createMultiplesampleMouvement(
  params: CreateMultiplesampleMouvement,
) {
  const { data } = await api.post<MultiplesampleMouvement>(baseRoute, params);
  return data;
}

export function useCreateMultiplesampleMouvement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createMultiplesampleMouvement,
    onSuccess: (_, { multiplesampleId }) => {
      queryClient.invalidateQueries({
        queryKey: mouvementsQueryKey(multiplesampleId),
      });
    },
  });
}

async function editMultiplesampleMouvement({
  mouvementId,
  mouvement,
}: {
  mouvementId: string;
  multiplesampleId: string;
  mouvement: EditMultiplesampleMouvement;
}) {
  const { data } = await api.put<MultiplesampleMouvement>(
    `${baseRoute}/${mouvementId}`,
    mouvement,
  );
  return data;
}

export function useEditMultiplesampleMouvement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: editMultiplesampleMouvement,
    onSuccess: (_, { multiplesampleId }) => {
      queryClient.invalidateQueries({
        queryKey: mouvementsQueryKey(multiplesampleId),
      });
    },
  });
}

async function deleteMultiplesampleMouvement({
  mouvementId,
}: {
  mouvementId: string;
  multiplesampleId: string;
}) {
  await api.delete(`${baseRoute}/${mouvementId}`);
}

export function useDeleteMultiplesampleMouvement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteMultiplesampleMouvement,
    onSuccess: (_, { multiplesampleId }) => {
      queryClient.invalidateQueries({
        queryKey: mouvementsQueryKey(multiplesampleId),
      });
    },
  });
}

async function duplicateMultiplesampleMouvement({
  mouvementId,
}: {
  mouvementId: string;
  multiplesampleId: string;
}) {
  const { data } = await api.post<MultiplesampleMouvement>(
    `${baseRoute}/${mouvementId}/duplicate`,
  );
  return data;
}

export function useDuplicateMultiplesampleMouvement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: duplicateMultiplesampleMouvement,
    onSuccess: (_, { multiplesampleId }) => {
      queryClient.invalidateQueries({
        queryKey: mouvementsQueryKey(multiplesampleId),
      });
    },
  });
}

async function updateMultiplesampleMouvementSequence({
  newSequence,
  multiplesampleId,
}: {
  newSequence: string[];
  multiplesampleId: string;
}) {
  await api.put(`${baseRoute}/sequence`, {
    multiplesampleId,
    sequence: newSequence,
  });
}

export function useUpdateMultiplesampleMouvementSequence() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateMultiplesampleMouvementSequence,
    onMutate: async ({ newSequence, multiplesampleId }) => {
      const key = mouvementsQueryKey(multiplesampleId);
      await queryClient.cancelQueries({ queryKey: key });

      const previous =
        queryClient.getQueryData<MultiplesampleMouvement[]>(key);

      if (previous) {
        const byId = new Map(previous.map((m) => [m.id, m]));
        const reordered = newSequence
          .map((id, index) => {
            const m = byId.get(id);
            return m ? { ...m, sequence: index } : undefined;
          })
          .filter((m): m is MultiplesampleMouvement => m !== undefined);
        queryClient.setQueryData(key, reordered);
      }

      return { previous };
    },
    onError: (_err, { multiplesampleId }, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          mouvementsQueryKey(multiplesampleId),
          context.previous,
        );
      }
    },
  });
}
